import { useState, useRef, useEffect } from 'react';
import {
    Camera, Image as ImageIcon, FileType, Combine, Scissors, Layers,
    Upload, Trash2, AlertCircle, Loader2, MapPin, MapPinOff,
} from 'lucide-react';
import { ACCENT as SECTION_COLOR } from '../../lib/uiTokens';
import { zmensitObrazek } from '../../lib/obrazek';

const isImage = (f) => f?.type?.startsWith('image/');
const MAX_STRAN = 8; // limit backendu (AIDocScanner čte max 8 stran)

const sklonDoklad = (n) => n === 1 ? 'doklad' : n < 5 ? 'doklady' : 'dokladů';
const sklonStrana = (n) => n < 5 ? 'strany' : 'stran';

/**
 * Sdílené jádro nahrávání dokladů — používá ho desktop (DokladyUploadModal)
 * i mobil (DokladyMobilUpload). Řeší: focení/výběr souborů → staging seskupení
 * (každý soubor doklad / sloučit do vícestránkového) → paralelní async upload
 * přes POST /finance/doklady/upload-doklad.
 *
 * Props:
 *   zdroj        'desktop' | 'mobil'
 *   initialFiles File[] — předvybrané soubory (desktop inbox)
 *   showGeo      bool — geo toggle (mobil)
 *   showPoznamka bool — pole poznámky (mobil)
 *   variant      'modal' | 'page' — velikosti/rozložení + tlačítko Zrušit
 *   onUploaded   (okCount) => void — po úspěšném nahrání (refresh hostitele)
 *   onClose      () => void — modal: zrušit / zavřít po plném úspěchu
 */
export default function DokladUploader({
    zdroj = 'desktop', initialFiles = null, showGeo = false, showPoznamka = false,
    variant = 'modal', onUploaded, onClose,
}) {
    const jePage = variant === 'page';
    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    // skupiny: pole polí File — každá skupina = jeden (více)stránkový doklad
    const [skupiny, setSkupiny] = useState(() => (initialFiles || []).map(f => [f]));
    const [vybrane, setVybrane] = useState(() => new Set());
    const [nahravam, setNahravam] = useState(false);
    const [chyby, setChyby] = useState([]);
    const [poznamka, setPoznamka] = useState('');
    const [geoEnabled, setGeoEnabled] = useState(false);
    const [geoPosice, setGeoPosice] = useState(null);
    const [geoChyba, setGeoChyba] = useState(null);

    const cameraInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const urlMapRef = useRef(new Map());

    const previewUrl = (f) => {
        if (!isImage(f)) return null;
        if (!urlMapRef.current.has(f)) urlMapRef.current.set(f, URL.createObjectURL(f));
        return urlMapRef.current.get(f);
    };
    useEffect(() => () => { for (const u of urlMapRef.current.values()) URL.revokeObjectURL(u); }, []);

    const pocetStran = skupiny.reduce((s, g) => s + g.length, 0);
    const pocetDokladu = skupiny.length;

    const handleFiles = (files) => {
        const nove = Array.from(files || []).map(f => [f]);
        if (nove.length) setSkupiny(prev => [...prev, ...nove]);
    };

    const toggle = (i) => setVybrane(prev => {
        const n = new Set(prev);
        n.has(i) ? n.delete(i) : n.add(i);
        return n;
    });

    // Sloučí zaškrtnuté skupiny do jedné (na pozici první vybrané), pořadí zachová
    const sloucitVybrane = () => {
        if (vybrane.size < 2) return;
        const spojene = [];
        skupiny.forEach((g, i) => { if (vybrane.has(i)) spojene.push(...g); });
        if (spojene.length > MAX_STRAN) {
            setChyby([`Jeden doklad může mít max ${MAX_STRAN} stran.`]);
            return;
        }
        const nove = [];
        let vlozeno = false;
        skupiny.forEach((g, i) => {
            if (vybrane.has(i)) {
                if (!vlozeno) { nove.push(spojene); vlozeno = true; }
            } else nove.push(g);
        });
        setSkupiny(nove);
        setVybrane(new Set());
        setChyby([]);
    };

    const kazdyZvlast = () => { setSkupiny(skupiny.flat().map(f => [f])); setVybrane(new Set()); setChyby([]); };
    const vseJako1 = () => {
        const vse = skupiny.flat();
        if (vse.length > MAX_STRAN) { setChyby([`Jeden doklad může mít max ${MAX_STRAN} stran.`]); return; }
        setSkupiny([vse]); setVybrane(new Set()); setChyby([]);
    };

    const odebratStranku = (gi, fi) => {
        const soubor = skupiny[gi]?.[fi];
        if (soubor && urlMapRef.current.has(soubor)) {
            URL.revokeObjectURL(urlMapRef.current.get(soubor));
            urlMapRef.current.delete(soubor);
        }
        const nove = skupiny.map((g, i) => i === gi ? g.filter((_, j) => j !== fi) : g).filter(g => g.length > 0);
        setSkupiny(nove);
        setVybrane(new Set());
    };

    const toggleGeo = () => {
        if (geoEnabled) { setGeoEnabled(false); setGeoPosice(null); setGeoChyba(null); return; }
        if (!navigator.geolocation) { setGeoChyba('Toto zařízení nepodporuje geolokaci'); return; }
        setGeoEnabled(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => { setGeoPosice({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGeoChyba(null); },
            (err) => { setGeoChyba(err.message || 'Nepodařilo se získat polohu'); setGeoEnabled(false); },
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
        );
    };

    const nahrat = async () => {
        if (pocetDokladu === 0) return;
        setNahravam(true);
        setChyby([]);

        const results = await Promise.allSettled(skupiny.map(async (g) => {
            const fd = new FormData();
            for (const f of g) fd.append('soubor[]', await zmensitObrazek(f));
            fd.append('zdroj', zdroj);
            if (geoPosice) { fd.append('geo_lat', geoPosice.lat); fd.append('geo_lng', geoPosice.lng); }
            fd.append('foto_porizeno_at', new Date(g[0].lastModified).toISOString());
            if (poznamka) fd.append('poznamka', poznamka);
            const r = await fetch('/api/finance/doklady/upload-doklad', {
                method: 'POST', headers: { 'X-CSRF-TOKEN': token }, body: fd,
            });
            if (!r.ok) {
                const e = await r.json().catch(() => ({}));
                const msg = r.status === 413 ? 'Soubor je příliš velký'
                    : r.status === 419 ? 'Vypršelo přihlášení — obnov stránku'
                    : (e.message || `Upload selhal (${r.status})`);
                throw new Error(msg);
            }
            return r.json();
        }));

        const okCount = results.filter(r => r.status === 'fulfilled').length;
        const failGroups = [];
        const failMsgs = [];
        results.forEach((r, i) => {
            if (r.status === 'rejected') { failGroups.push(skupiny[i]); failMsgs.push(r.reason?.message || 'Chyba nahrávání'); }
        });

        if (okCount > 0) onUploaded?.(okCount);

        if (failGroups.length) {
            setSkupiny(failGroups); // ponech jen neúspěšné → retry bez duplicit
            setVybrane(new Set());
            setChyby([...new Set(failMsgs)]);
            setNahravam(false);
            return;
        }

        // Plný úspěch
        setNahravam(false);
        if (jePage) { setSkupiny([]); setPoznamka(''); }
        else onClose?.();
    };

    const btn = jePage ? 'py-4 text-base rounded-2xl' : 'py-2 text-sm rounded-lg';
    const nahledPx = jePage ? 'w-14 h-14' : 'w-11 h-11';

    return (
        <div className={jePage ? 'space-y-4' : 'flex flex-col min-h-0'}>
            {/* Skryté inputy */}
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} className="hidden" />
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple
                onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} className="hidden" />

            {/* Přidání souborů */}
            <div className={jePage ? 'grid grid-cols-2 gap-3' : 'flex gap-2 px-5 pt-4'}>
                <button onClick={() => cameraInputRef.current?.click()} disabled={nahravam}
                    className={jePage
                        ? 'flex flex-col items-center justify-center gap-2 rounded-2xl p-6 text-white font-medium active:scale-95'
                        : 'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white'}
                    style={{ background: SECTION_COLOR }}>
                    <Camera size={jePage ? 32 : 16} strokeWidth={1.5} />
                    <span className={jePage ? 'text-sm' : ''}>Vyfotit</span>
                </button>
                <button onClick={() => fileInputRef.current?.click()} disabled={nahravam}
                    className={jePage
                        ? 'flex flex-col items-center justify-center gap-2 rounded-2xl p-6 font-medium active:scale-95'
                        : 'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium'}
                    style={jePage
                        ? { background: 'var(--surface-raised)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }
                        : { background: `${SECTION_COLOR}14`, color: SECTION_COLOR, border: `1px solid ${SECTION_COLOR}33` }}>
                    <ImageIcon size={jePage ? 32 : 16} strokeWidth={1.5} />
                    <span className={jePage ? 'text-sm' : ''}>{jePage ? 'Z galerie' : 'Vybrat soubory'}</span>
                </button>
            </div>

            {/* Geo toggle (mobil) */}
            {showGeo && (
                <div className={jePage ? '' : 'px-5'}>
                    <button onClick={toggleGeo} disabled={nahravam}
                        className="w-full flex items-center justify-between rounded-xl p-3 text-sm"
                        style={{
                            background: geoEnabled ? 'rgba(16,185,129,0.08)' : 'var(--surface-raised)',
                            border: `1px solid ${geoEnabled ? 'rgba(16,185,129,0.25)' : 'var(--border-default)'}`,
                            color: 'var(--text-primary)',
                        }}>
                        <div className="flex items-center gap-2">
                            {geoEnabled ? <MapPin size={16} style={{ color: '#10b981' }} /> : <MapPinOff size={16} style={{ color: 'var(--text-tertiary)' }} />}
                            <span>{geoEnabled ? (geoPosice ? `Poloha: ${geoPosice.lat.toFixed(4)}, ${geoPosice.lng.toFixed(4)}` : 'Získávám polohu…') : 'Uložit polohu k uploadu'}</span>
                        </div>
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{geoEnabled ? 'zapnuto' : 'vypnuto'}</span>
                    </button>
                    {geoChyba && <div className="text-xs mt-1 px-1" style={{ color: '#ef4444' }}>{geoChyba}</div>}
                </div>
            )}

            {/* Ovládání seskupení */}
            {pocetStran > 0 && (
                <div className={`flex flex-wrap items-center gap-2 ${jePage ? '' : 'px-5 py-2 border-b'}`}
                    style={jePage ? {} : { borderColor: 'var(--border)' }}>
                    <span className="text-[11px] mr-1" style={{ color: 'var(--text-tertiary)' }}>
                        {pocetDokladu} {sklonDoklad(pocetDokladu)} k nahrání
                    </span>
                    <button onClick={sloucitVybrane} disabled={vybrane.size < 2}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium disabled:opacity-40"
                        style={{ background: `${SECTION_COLOR}14`, color: SECTION_COLOR, border: `1px solid ${SECTION_COLOR}33` }}
                        title="Sloučit zaškrtnuté do jednoho vícestránkového dokladu">
                        <Combine size={14} /> Sloučit vybrané ({vybrane.size})
                    </button>
                    <button onClick={kazdyZvlast}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium"
                        style={{ background: 'var(--surface-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}
                        title="Každý soubor jako samostatný doklad">
                        <Scissors size={14} /> Každý zvlášť
                    </button>
                    {pocetStran > 1 && (
                        <button onClick={vseJako1}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium"
                            style={{ background: 'var(--surface-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}
                            title="Všechny soubory jako jeden vícestránkový doklad">
                            <Layers size={14} /> Vše jako 1 doklad
                        </button>
                    )}
                </div>
            )}

            {/* Seznam skupin */}
            {pocetStran > 0 && (
                <div className={jePage ? 'space-y-2' : 'flex-1 overflow-y-auto px-5 py-3 space-y-2'}>
                    {skupiny.map((g, gi) => (
                        <div key={gi} className="flex items-center gap-3 p-2.5 rounded-xl cursor-pointer"
                            style={{
                                border: vybrane.has(gi) ? `1.5px solid ${SECTION_COLOR}` : '1px solid var(--border-default)',
                                background: vybrane.has(gi) ? `${SECTION_COLOR}0d` : 'var(--surface-raised)',
                            }}
                            onClick={() => toggle(gi)}>
                            <input type="checkbox" checked={vybrane.has(gi)} onChange={() => toggle(gi)}
                                onClick={(e) => e.stopPropagation()} className="w-4 h-4 shrink-0" style={{ accentColor: SECTION_COLOR }} />
                            <div className="flex items-center gap-1.5 shrink-0">
                                {g.slice(0, 4).map((f, fi) => (
                                    <div key={fi} className={`relative ${nahledPx} rounded-lg overflow-hidden shrink-0`}
                                        style={{ background: 'var(--surface-base)', border: '1px solid var(--border-default)' }}>
                                        {previewUrl(f)
                                            ? <img src={previewUrl(f)} alt="" className="w-full h-full object-cover" />
                                            : <div className="w-full h-full flex items-center justify-center"><FileType size={18} style={{ color: 'var(--text-tertiary)' }} /></div>}
                                        {g.length > 1 && (
                                            <button onClick={(e) => { e.stopPropagation(); odebratStranku(gi, fi); }}
                                                className="absolute top-0 right-0 p-0.5 rounded-bl-lg" style={{ background: 'rgba(0,0,0,0.55)' }} title="Odebrat stránku">
                                                <Trash2 size={10} color="#fff" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {g.length > 4 && <span className="text-[11px] font-medium" style={{ color: 'var(--text-tertiary)' }}>+{g.length - 4}</span>}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-[12.5px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                                    Doklad {gi + 1}{g.length > 1 ? ` · ${g.length} ${sklonStrana(g.length)}` : ''}
                                </div>
                                <div className="text-[11px] truncate" style={{ color: 'var(--text-tertiary)' }}>{g.map(f => f.name).join(', ')}</div>
                            </div>
                            {g.length === 1 && !nahravam && (
                                <button onClick={(e) => { e.stopPropagation(); odebratStranku(gi, 0); }} className="p-1 shrink-0" title="Odebrat">
                                    <Trash2 size={14} style={{ color: 'var(--text-tertiary)' }} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Poznámka (mobil) */}
            {showPoznamka && pocetStran > 0 && (
                <textarea value={poznamka} onChange={e => setPoznamka(e.target.value)}
                    placeholder="Volitelná poznámka (např. „Nákup pro akci“)" rows={2}
                    className="w-full rounded-xl p-3 text-sm"
                    style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)', color: 'var(--text-primary)', resize: 'vertical' }} />
            )}

            {/* Chyby */}
            {chyby.length > 0 && (
                <div className={`px-3 py-2 rounded-lg flex items-start gap-2 text-[12px] ${jePage ? '' : 'mx-5'}`}
                    style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>
                    <AlertCircle size={15} className="shrink-0 mt-0.5" />
                    <div>{chyby.join('; ')}</div>
                </div>
            )}

            {/* Akce */}
            {pocetStran > 0 && (
                <div className={jePage ? '' : 'flex items-center justify-end gap-2 px-5 py-3.5 border-t'}
                    style={jePage ? {} : { borderColor: 'var(--border)' }}>
                    {!jePage && (
                        <button onClick={onClose} disabled={nahravam}
                            className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                            style={{ background: 'var(--surface-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                            Zrušit
                        </button>
                    )}
                    <button onClick={nahrat} disabled={nahravam || pocetDokladu === 0}
                        className={`inline-flex items-center justify-center gap-2 font-semibold text-white disabled:opacity-50 px-4 ${btn} ${jePage ? 'w-full active:scale-95' : ''}`}
                        style={{ background: SECTION_COLOR }}>
                        {nahravam ? <Loader2 size={jePage ? 20 : 16} className="animate-spin" /> : <Upload size={jePage ? 20 : 16} />}
                        Nahrát {pocetDokladu} {sklonDoklad(pocetDokladu)}
                    </button>
                </div>
            )}
        </div>
    );
}
